import { createSlice } from '@reduxjs/toolkit';

import { setState } from '@app/helpers/store';
import { DASHBOARD_SLICE_PATHS } from './constants';

const emptySlice = () => ({
  isFetching: false,
  // Whether a fetch attempt has finished, regardless of outcome — an errored
  // slice counts as settled, or one failing endpoint would hold the whole tab
  // on its loading state forever.
  hasCompleted: false,
  data: null,
  error: null,
  // The scope the settled outcome above belongs to, so a later request can tell
  // a refresh of the same client from a switch to another one.
  subsidiaryId: null,
});

const buildInitialState = () =>
  Object.entries(DASHBOARD_SLICE_PATHS).reduce(
    (dashboards, [id, slicePaths]) => ({
      ...dashboards,
      [id]: Object.keys(slicePaths).reduce(
        (slices, name) => ({ ...slices, [name]: emptySlice() }),
        {},
      ),
    }),
    {},
  );

const insightsViewsSlice = createSlice({
  name: 'insightsViews',
  initialState: buildInitialState(),
  reducers: {
    fetchDashboardRequest(state, { payload: { id, subsidiaryId = null } }) {
      const dashboard = state[id];

      if (!dashboard) {
        return;
      }

      Object.keys(dashboard).forEach(name => {
        const slice = dashboard[name];
        // A tab panel unmounts its content, so returning to a tab remounts the
        // dashboard and re-requests what the store already holds. Keeping the
        // settled outcome leaves `hasCompleted` true, which is how web-charts
        // tells a refresh from a cold load: it draws a small spinner over the
        // previous numbers rather than replacing them with a full skeleton.
        //
        // Only for the same client, though. The subsidiary is part of the
        // request, so a payload held across a scope change is another client's
        // numbers on screen while theirs is in flight.
        const isSameScope = slice.subsidiaryId === subsidiaryId;

        setState(slice, {
          isFetching: true,
          hasCompleted: isSameScope && slice.hasCompleted,
          data: isSameScope ? slice.data : null,
          error: isSameScope ? slice.error : null,
          subsidiaryId,
        });
      });
    },
    fetchSliceSuccess(state, { payload: { id, slice, data } }) {
      if (!state[id]?.[slice]) {
        return;
      }

      setState(state[id][slice], {
        isFetching: false,
        hasCompleted: true,
        data,
        error: null,
      });
    },
    fetchSliceError(state, { payload: { id, slice, error } }) {
      if (!state[id]?.[slice]) {
        return;
      }

      setState(state[id][slice], {
        isFetching: false,
        hasCompleted: true,
        data: null,
        error,
      });
    },
  },
});

const { actions, reducer } = insightsViewsSlice;

export const { fetchDashboardRequest, fetchSliceSuccess, fetchSliceError } =
  actions;

export default reducer;
